export const GLOBAL_SEARCH_MIN_LENGTH = 2;
export const GLOBAL_SEARCH_PER_TYPE_LIMIT = 8;
export const GLOBAL_SEARCH_OVERALL_LIMIT = 25;

export type GlobalSearchResultType =
  | "club_player"
  | "group"
  | "tournament_session"
  | "tournament_player"
  | "funder"
  | "funding_appeal"
  | "funding_outreach";

export interface GlobalSearchRawRow {
  id: string;
  type: GlobalSearchResultType;
  title: string;
  subtitle: string | null;
  club_id: string;
  club_name: string | null;
  tournament_session_id: string | null;
  match_name: string | null;
  match_email: string | null;
  match_phone: string | null;
}

export interface GlobalSearchResult {
  id: string;
  type: GlobalSearchResultType;
  title: string;
  subtitle: string | null;
  clubId: string;
  clubName: string | null;
  tournamentSessionId: string | null;
}

export const GLOBAL_SEARCH_TYPE_LABELS: Record<GlobalSearchResultType, string> = {
  club_player: "Member",
  group: "Group",
  tournament_session: "Tournament",
  tournament_player: "Tournament player",
  funder: "Funder",
  funding_appeal: "Funding appeal",
  funding_outreach: "Outreach",
};

export type GlobalSearchContextKind = "club" | "tournament";

export function getSessionNavigationContext(
  pathname: string,
  token: string | undefined,
  currentClubId: string | null | undefined,
  resultClubId: string,
): { token: string; clubId: string } | null {
  if (!/^\/t\/[^/]+/.test(pathname)) return null;
  if (!token) return null;
  if (!currentClubId || currentClubId !== resultClubId) return null;
  return { token, clubId: resultClubId };
}

export interface GlobalSearchRankingOptions {
  kind: GlobalSearchContextKind;
  clubId?: string | null;
  tournamentSessionId?: string | null;
}

const CLUB_CONTEXT_TYPE_ORDER: GlobalSearchResultType[] = [
  "club_player",
  "group",
  "tournament_session",
  "tournament_player",
  "funder",
  "funding_appeal",
  "funding_outreach",
];

const TOURNAMENT_CONTEXT_TYPE_ORDER: GlobalSearchResultType[] = [
  "tournament_player",
  "tournament_session",
  "club_player",
  "group",
  "funder",
  "funding_appeal",
  "funding_outreach",
];

export function globalSearchTypeOrderIndex(
  type: GlobalSearchResultType,
  kind: GlobalSearchContextKind,
): number {
  const order = kind === "tournament" ? TOURNAMENT_CONTEXT_TYPE_ORDER : CLUB_CONTEXT_TYPE_ORDER;
  const index = order.indexOf(type);
  return index === -1 ? order.length : index;
}

function isCurrentClubMatch(clubId: string | null | undefined, resultClubId: string): boolean {
  return Boolean(clubId && resultClubId === clubId);
}

function isCurrentTournamentMatch(
  tournamentSessionId: string | null | undefined,
  resultTournamentSessionId: string | null,
): boolean {
  return Boolean(tournamentSessionId && resultTournamentSessionId === tournamentSessionId);
}

interface ScoredGlobalSearchRow {
  row: GlobalSearchRawRow;
  score: number;
}

function compareScoredRows(
  a: ScoredGlobalSearchRow,
  b: ScoredGlobalSearchRow,
  options?: GlobalSearchRankingOptions,
): number {
  const kind = options?.kind ?? "club";

  const typeDiff =
    globalSearchTypeOrderIndex(a.row.type, kind) - globalSearchTypeOrderIndex(b.row.type, kind);
  if (typeDiff !== 0) return typeDiff;

  const aCurrentClub = isCurrentClubMatch(options?.clubId, a.row.club_id) ? 1 : 0;
  const bCurrentClub = isCurrentClubMatch(options?.clubId, b.row.club_id) ? 1 : 0;
  if (bCurrentClub !== aCurrentClub) return bCurrentClub - aCurrentClub;

  const aCurrentTournament = isCurrentTournamentMatch(
    options?.tournamentSessionId,
    a.row.tournament_session_id,
  )
    ? 1
    : 0;
  const bCurrentTournament = isCurrentTournamentMatch(
    options?.tournamentSessionId,
    b.row.tournament_session_id,
  )
    ? 1
    : 0;
  if (bCurrentTournament !== aCurrentTournament) return bCurrentTournament - aCurrentTournament;

  return b.score - a.score || a.row.title.localeCompare(b.row.title);
}

export function compareGlobalSearchResults(
  a: GlobalSearchResult,
  b: GlobalSearchResult,
  options?: GlobalSearchRankingOptions,
): number {
  const kind = options?.kind ?? "club";

  const typeDiff = globalSearchTypeOrderIndex(a.type, kind) - globalSearchTypeOrderIndex(b.type, kind);
  if (typeDiff !== 0) return typeDiff;

  const aCurrentClub = isCurrentClubMatch(options?.clubId, a.clubId) ? 1 : 0;
  const bCurrentClub = isCurrentClubMatch(options?.clubId, b.clubId) ? 1 : 0;
  if (bCurrentClub !== aCurrentClub) return bCurrentClub - aCurrentClub;

  const aCurrentTournament = isCurrentTournamentMatch(options?.tournamentSessionId, a.tournamentSessionId)
    ? 1
    : 0;
  const bCurrentTournament = isCurrentTournamentMatch(options?.tournamentSessionId, b.tournamentSessionId)
    ? 1
    : 0;
  if (bCurrentTournament !== aCurrentTournament) return bCurrentTournament - aCurrentTournament;

  return a.title.localeCompare(b.title);
}

export function groupGlobalSearchResults(
  results: GlobalSearchResult[],
  options?: GlobalSearchRankingOptions,
): Array<{ type: GlobalSearchResultType; label: string; results: GlobalSearchResult[] }> {
  const kind = options?.kind ?? "club";
  const groups = new Map<GlobalSearchResultType, GlobalSearchResult[]>();

  for (const result of results) {
    const existing = groups.get(result.type) ?? [];
    existing.push(result);
    groups.set(result.type, existing);
  }

  return Array.from(groups.entries())
    .sort(
      ([typeA], [typeB]) => globalSearchTypeOrderIndex(typeA, kind) - globalSearchTypeOrderIndex(typeB, kind),
    )
    .map(([type, typeResults]) => ({
      type,
      label: GLOBAL_SEARCH_TYPE_LABELS[type],
      results: [...typeResults].sort((a, b) => compareGlobalSearchResults(a, b, options)),
    }));
}

export function normalizeGlobalSearchRow(row: GlobalSearchRawRow): GlobalSearchResult {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    subtitle: row.subtitle,
    clubId: row.club_id,
    clubName: row.club_name,
    tournamentSessionId: row.tournament_session_id,
  };
}

function fieldScore(value: string | null | undefined, search: string): number {
  if (!value) return 0;
  const normalized = value.toLowerCase().trim();
  if (!normalized) return 0;
  if (normalized === search) return 100;
  if (normalized.startsWith(search)) return 50;
  if (normalized.includes(search)) return 10;
  return 0;
}

export function rankGlobalSearchRows(
  rows: GlobalSearchRawRow[],
  search: string,
  options?: GlobalSearchRankingOptions,
): GlobalSearchResult[] {
  const normalizedSearch = search.toLowerCase().trim();
  if (normalizedSearch.length < GLOBAL_SEARCH_MIN_LENGTH) {
    return [];
  }

  const scored = rows.map((row) => {
    const scores = [
      fieldScore(row.match_name, normalizedSearch),
      fieldScore(row.match_email, normalizedSearch),
      fieldScore(row.match_phone, normalizedSearch),
      fieldScore(row.title, normalizedSearch),
      fieldScore(row.subtitle, normalizedSearch),
    ];
    return {
      row,
      score: Math.max(...scores),
    };
  });

  return scored
    .filter((entry) => entry.score > 0)
    .sort((a, b) => compareScoredRows(a, b, options))
    .slice(0, GLOBAL_SEARCH_OVERALL_LIMIT)
    .map((entry) => normalizeGlobalSearchRow(entry.row));
}
